import React from 'react';
import { Input as AntInput, InputNumber as AntInputNumber, Select as AntSelect, Button as AntButton } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

// Date field editor component
function DateFieldEditor({ value, onChange, placeholder = 'YYYY-MM-DD', disabled = false }) {
  const [open, setOpen] = React.useState(false);
  
  const normalized = normalizeYmdInitial(value);
  
  React.useEffect(() => {
    const next = normalizeYmdInitial(value);
    if (next && next !== value) {
      onChange(next);
    }
  }, [value, onChange]);
  
  const str = normalized ?? '';
  const invalid = str ? !isValidYmd(str) : false;
  
  return (
    <div className="relative w-full">
      <AntInput
        placeholder={placeholder}
        value={str}
        inputMode="numeric"
        maxLength={10}
        status={invalid ? 'error' : ''}
        onChange={(e) => onChange(formatYmdInput(e.target.value))}
        disabled={disabled}
      />
      {invalid && <div className="text-[11px] text-red-600 mt-1">잘못된 날짜입니다</div>}
    </div>
  );
}

function formatYmdInput(raw) {
  const digits = String(raw || '').replace(/\D+/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
}

function normalizeYmdInitial(raw) {
  const s = (raw ?? '').toString().trim();
  if (!s) return '';
  if (/^\d{8}$/.test(s)) return formatYmdInput(s);
  return s;
}

function isValidYmd(str) {
  if (!str || typeof str !== 'string') return false;
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 1900 || y > 2100) return false;
  if (mo < 1 || mo > 12) return false;
  const mdays = [31, (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const maxd = mdays[mo - 1];
  if (d < 1 || d > maxd) return false;
  return true;
}

function formatAgeGroupDisplay(val) {
  const s = (val ?? '').toString().trim();
  if (/^\d+$/.test(s)) return `${s}세`;
  return s;
}

const TravelerAccordionCard = ({
  traveler,
  index,
  orderCancelled,
  isExpanded,
  onToggle,
  onUpdate,
  onDelete,
  onCancel,
  onRestore,
  editLocked,
  isSelected,
  onSelect,
  passportUrl,
  uploadingPassport,
  onUploadPassport,
  onDeletePassport,
  allowPassportUpload,
}) => {
  const isCancelled = traveler?.여행객종류 === '취소';
  const strike = !!orderCancelled;
  const basePrice = Number(traveler?.상품금액원화) || 0;
  const extraSalePrice = Number(traveler?.택스및유류원화) || 0;
  const totalPrice = basePrice + extraSalePrice;
  
  const ageOptions = [
    { value: '성인', label: '성인' },
    ...Array.from({ length: 19 }, (_, n) => ({ value: String(n), label: `${n}세` })),
  ];
  
  const travelerNoRaw = traveler?.여행객번호 || traveler?.여행객키;
  const idStr = String(travelerNoRaw ?? '');
  const isNumeric = /^\d+$/.test(idStr);
  const isNew = !isNumeric;
  const fileInputRef = React.useRef(null);

  return (
    <div className={`border rounded-lg overflow-hidden ${isCancelled ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div 
        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${isCancelled ? 'bg-red-100' : 'bg-gray-50'}`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {!editLocked && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect(e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">{index + 1}</span>
            {traveler?.룸번호 != null && (
              <span className="text-[10px] bg-gray-100 text-gray-700 border px-1.5 py-0.5 rounded font-medium">
                루밍 {traveler.룸번호}번
              </span>
            )}
            <span className={`text-sm font-medium text-gray-900 ${strike ? 'line-through decoration-red-500' : ''}`}>{traveler?.한글이름 || '(이름 없음)'}</span>
            
            {isCancelled && (
              <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded font-medium">취소</span>
            )}
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>{formatAgeGroupDisplay(traveler?.연령구분)}</span>
              <span>·</span>
              <span>{traveler?.성별 || '-'}</span>
              {traveler?.영문성 && traveler?.영문이름 && (
                <>
                  <span>·</span>
                  <span>{traveler?.영문성} {traveler?.영문이름}</span>
                </>
              )}
            </div>
            {traveler?.여행객비고 && (
              <div className="text-xs text-gray-500 truncate max-w-xs">
                {traveler?.여행객비고}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-end leading-tight">
            <span className={`text-sm font-semibold text-blue-600 tabular-nums ${strike ? 'line-through decoration-red-500' : ''}`}>
              {totalPrice.toLocaleString('ko-KR')}원
            </span>
            {!isCancelled && (
              <span className={`text-[11px] text-gray-500 tabular-nums ${strike ? 'line-through decoration-red-500' : ''}`}>
                {basePrice.toLocaleString('ko-KR')}원
                {extraSalePrice > 0 ? ` +(추가판매) ${extraSalePrice.toLocaleString('ko-KR')}원` : ''}
              </span>
            )}
          </div>
          {isExpanded ? <UpOutlined className="text-gray-400" /> : <DownOutlined className="text-gray-400" />}
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="space-y-4">
            {/* 기본 정보 */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 pb-1 ">기본 정보</h4>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">예약 시 선택 연령</span>
                  <div className="flex-1">
                    {!isNew ? (
                      <div className="h-8 flex items-center px-3 bg-gray-50 border border-gray-200 rounded text-sm">
                        {formatAgeGroupDisplay(traveler?.연령구분)}
                      </div>
                    ) : (
                      <AntSelect
                        value={traveler?.연령구분 ?? '성인'}
                        options={ageOptions}
                        disabled={editLocked}
                        onChange={(val) => onUpdate({ 연령구분: val })}
                        style={{ width: '100%' }}
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="ml-5 w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">한글이름</span>
                  <AntInput
                    className="flex-1"
                    value={traveler?.한글이름 ?? ''}
                    disabled={editLocked}
                    onChange={(e) => onUpdate({ 한글이름: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">성별</span>
                  <AntSelect
                    value={traveler?.성별 ?? undefined}
                    placeholder="선택"
                    options={[{ value: '남성', label: '남성' }, { value: '여성', label: '여성' }]}
                    disabled={editLocked}
                    onChange={(val) => onUpdate({ 성별: val })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="ml-5 w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">생년월일</span>
                  <div className="flex-1">
                    <DateFieldEditor
                      value={traveler?.생년월일}
                      disabled={editLocked}
                      onChange={(val) => onUpdate({ 생년월일: val })}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* 여권 정보 */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 pb-1 ">여권 정보</h4>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">영문성</span>
                  <AntInput
                    className="flex-1"
                    value={traveler?.영문성 ?? ''}
                    disabled={editLocked}
                    onChange={(e) => onUpdate({ 영문성: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="ml-5 w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">영문이름</span>
                  <AntInput
                    className="flex-1"
                    value={traveler?.영문이름 ?? ''}
                    disabled={editLocked}
                    onChange={(e) => onUpdate({ 영문이름: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">여권번호</span>
                  <AntInput
                    className="flex-1"
                    value={traveler?.여권번호 ?? ''}
                    disabled={editLocked}
                    onChange={(e) => onUpdate({ 여권번호: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="ml-5 w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">여권만료일</span>
                  <div className="flex-1">
                    <DateFieldEditor
                      value={traveler?.여권만료일}
                      disabled={editLocked}
                      onChange={(val) => onUpdate({ 여권만료일: val })}
                    />
                  </div>
                </div>
                {allowPassportUpload && (
                  <div className="flex items-center gap-2">
                    <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">여권사본</span>
                    <div className="flex-1 flex items-center gap-2">
                      {passportUrl && (
                        <a
                          href={passportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 truncate max-w-[160px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          여권사본 보기
                        </a>
                      )}
                      {!editLocked && (
                        <AntButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (fileInputRef.current) {
                              fileInputRef.current.click();
                            }
                          }}
                          loading={!!uploadingPassport}
                        >
                          {uploadingPassport ? '판독중' : (passportUrl ? '변경' : '업로드')}
                        </AntButton>
                      )}
                      {passportUrl && !editLocked && (
                        <AntButton
                          size="small"
                          danger
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onDeletePassport) {
                              onDeletePassport();
                            }
                          }}
                        >
                          삭제
                        </AntButton>
                      )}
                      {!editLocked && (
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (!file) return;
                            if (onUploadPassport) {
                              onUploadPassport(file);
                            }
                            e.target.value = '';
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* 연락처 정보 */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 pb-1 ">연락처 정보</h4>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">휴대폰</span>
                  <AntInput
                    className="flex-1"
                    value={traveler?.휴대폰 ?? ''}
                    disabled={editLocked}
                    onChange={(e) => onUpdate({ 휴대폰: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="ml-5 w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">이메일</span>
                  <AntInput
                    className="flex-1"
                    value={traveler?.이메일 ?? ''}
                    disabled={editLocked}
                    onChange={(e) => onUpdate({ 이메일: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            {/* 상품금액 정보 */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 pb-1 ">상품금액 정보</h4>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">{isCancelled ? '취소료' : '상품금액'}</span>
                  <AntInputNumber
                    min={0}
                    value={Number(traveler?.상품금액원화) || 0}
                    disabled={editLocked}
                    onChange={(val) => onUpdate({ 상품금액원화: Number(val) || 0 })}
                    style={{ width: '100%' }}
                    formatter={(val) => {
                      if (typeof val === 'number') return `${val.toLocaleString('ko-KR')} 원`;
                      const s = String(val || '');
                      return `${s.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} 원`;
                    }}
                    parser={(val) => (val ? val.toString().replace(/[^\d]/g, '') : '')}
                  />
                </div>
                {!isCancelled && (
                  <div className="flex items-center gap-2">
                    <span className="ml-5 w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">추가판매</span>
                    <AntInputNumber
                      value={Number(traveler?.택스및유류원화) || 0}
                      disabled={editLocked}
                      onChange={(val) => onUpdate({ 택스및유류원화: Number(val) || 0 })}
                      style={{ width: '100%' }}
                      formatter={(val) => {
                        if (typeof val === 'number') return `${val.toLocaleString('ko-KR')} 원`;
                        const s = String(val || '');
                        return `${s.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} 원`;
                      }}
                      parser={(val) => (val ? val.toString().replace(/[^\d]/g, '') : '')}
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5">총 판매가</span>
                  <div className="h-8 flex items-center px-3 bg-blue-50 border border-blue-200 rounded text-sm font-semibold text-blue-700 tabular-nums flex-1">
                    {totalPrice.toLocaleString('ko-KR')} 원
                  </div>
                </div>
              </div>
            </div>
            
            {/* 비고 */}
            <div>
              <div className="flex items-start gap-2">
                <span className="w-24 flex-none truncate text-[11px] text-gray-600 py-0.5 mt-0.5">여행객비고</span>
                <AntInput.TextArea
                  size="small"
                  className="flex-1"
                  value={traveler?.여행객비고 ?? ''}
                  disabled={editLocked}
                  onChange={(e) => onUpdate({ 여행객비고: e.target.value })}
                  rows={3}
                  placeholder="비고 사항을 입력하세요"
                />
              </div>
            </div>

            {/* 하단 작업 버튼 */}
            {!editLocked && (
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 mt-2">
                {isCancelled ? (
                  <AntButton size="small" onClick={(e) => { e.stopPropagation(); onRestore(); }}>
                    취소 해제
                  </AntButton>
                ) : (
                  <AntButton size="small" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
                    취소 처리
                  </AntButton>
                )}
                <AntButton
                  size="small"
                  danger
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                >
                  완전 삭제
                </AntButton>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelerAccordionCard;
